/**
 * 左侧筛选面板（对齐高保真原型 `#filter`）。
 *
 * 原型 `renderFilters()` 只有 **3 组**：
 *   健康状态（全部 / 健康 / 预警 / 告警）
 *   项目类型（全部 + 从项目数据动态取）
 *   任务形态（全部 + 从任务数据的 categoryName 动态取，8 种）
 *
 * ⚠️ 原型没有「大区」「全量概览」「项目状态」——已删除。
 * ⚠️ 面板标题为「筛选」，带折叠按钮 `−`（`#filter .fh`）。
 * ⚠️ 「＋ 新建项目」入口保留（产品入口，测试要求仍在）。
 */

import { useState } from 'react';
import Button from '../../components/Button';

export interface FilterOption {
  value: string;
  label: string;
}

/** ⭐ P6.54 搜索结果条目（按项目名称/代码匹配） */
export interface ProjectSearchResult {
  id: string;
  name: string;
  code: string;
  provinceName: string;
}

const HEALTH_OPTIONS: ReadonlyArray<{ value?: string; label: string }> = [
  { value: undefined, label: '全部' },
  { value: 'HEALTHY', label: '健康' },
  { value: 'WARNING', label: '预警' },
  { value: 'ALERT', label: '告警' },
];

export interface FilterPanelProps {
  healthLevel?: string;
  projectType?: string;
  taskForm?: string;
  onHealthLevel: (value: string | undefined) => void;
  onProjectType: (value: string | undefined) => void;
  onTaskForm: (value: string | undefined) => void;
  /** 项目类型选项：值来自真实项目数据，label 为中文名 */
  projectTypeOptions: readonly FilterOption[];
  /** 任务形态选项：来自真实任务的 categoryName */
  taskFormOptions: readonly string[];
  /**
   * 「＋ 新建项目」入口（可选）。
   * 无 `project:create` 权限时由 MapView 传 undefined —— 整项不渲染。
   */
  onCreateProject?: () => void;

  // ── ⭐ P6.54 项目搜索（按名称 / 代码；遵守数据范围）──
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  searchResults: readonly ProjectSearchResult[];
  /** 点击结果：地图定位并聚焦该项目 */
  onSelectSearchResult: (id: string) => void;
  /** 点击「星图 →」：直接进入该项目星图 */
  onOpenSearchResultStar: (id: string) => void;
}

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={active ? 'chip on' : 'chip'} onClick={onClick}>
      {label}
    </button>
  );
}

export default function FilterPanel({
  healthLevel,
  projectType,
  taskForm,
  onHealthLevel,
  onProjectType,
  onTaskForm,
  projectTypeOptions,
  taskFormOptions,
  onCreateProject,
  searchValue,
  onSearchChange,
  onSearchClear,
  searchResults,
  onSelectSearchResult,
  onOpenSearchResultStar,
}: FilterPanelProps) {
  const [folded, setFolded] = useState(false);
  const searching = searchValue.trim().length > 0;

  return (
    <div className={folded ? 'map-filter folded' : 'map-filter'}>
      {onCreateProject ? (
        <Button
          variant="primary"
          block
          className="map-newproj"
          data-testid="project-new"
          onClick={onCreateProject}
        >
          ＋ 新建项目
        </Button>
      ) : null}

      {/* ⭐ P6.54 项目搜索（按名称 / 代码；数据范围由后端 scoped 列表保证） */}
      <div className="map-search">
        <span className="map-search__icon" aria-hidden="true">
          ⌕
        </span>
        <input
          type="text"
          className="map-search__input"
          data-testid="map-project-search"
          placeholder="搜索项目名称 / 代码"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="搜索项目名称或代码"
        />
        {searching && (
          <button
            type="button"
            className="map-search__clear"
            data-testid="map-project-search-clear"
            aria-label="清空搜索"
            onClick={onSearchClear}
          >
            ✕
          </button>
        )}
        {searching && (
          <div className="map-search__results" role="listbox" data-testid="map-search-results">
            {searchResults.length === 0 ? (
              <div className="map-search__empty" data-testid="map-search-empty">
                没有匹配「{searchValue.trim()}」的项目
              </div>
            ) : (
              searchResults.map((r) => (
                <div className="map-search__row" key={r.id}>
                  <button
                    type="button"
                    className="map-search__hit"
                    role="option"
                    aria-selected={false}
                    data-testid={`map-search-hit-${r.id}`}
                    onClick={() => onSelectSearchResult(r.id)}
                  >
                    <b>{r.name}</b>
                    <span>
                      {r.code} · {r.provinceName}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="map-search__star"
                    data-testid={`map-search-star-${r.id}`}
                    title="进入该项目星图"
                    onClick={() => onOpenSearchResultStar(r.id)}
                  >
                    星图 →
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="fh">
        <span>筛选</span>
        <span
          className="tg"
          role="button"
          tabIndex={0}
          aria-label={folded ? '展开筛选' : '折叠筛选'}
          onClick={() => setFolded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setFolded((v) => !v);
          }}
        >
          {folded ? '+' : '−'}
        </span>
      </div>

      <div className="fb">
        {/* ① 健康状态 */}
        <div className="fg">
          <div className="fl">健康状态</div>
          <div className="chiprow">
            {HEALTH_OPTIONS.map((o) => (
              <Chip
                key={o.label}
                label={o.label}
                active={(healthLevel ?? undefined) === o.value}
                onClick={() => onHealthLevel(o.value)}
              />
            ))}
          </div>
        </div>

        {/* ② 项目类型（真实数据动态生成） */}
        <div className="fg">
          <div className="fl">项目类型</div>
          <div className="chiprow">
            <Chip
              label="全部"
              active={projectType === undefined}
              onClick={() => onProjectType(undefined)}
            />
            {projectTypeOptions.map((o) => (
              <Chip
                key={o.value}
                label={o.label}
                active={projectType === o.value}
                onClick={() => onProjectType(o.value)}
              />
            ))}
          </div>
        </div>

        {/* ③ 任务形态（真实数据动态生成） */}
        <div className="fg fg--last">
          <div className="fl">任务形态</div>
          <div className="chiprow">
            <Chip label="全部" active={taskForm === undefined} onClick={() => onTaskForm(undefined)} />
            {taskFormOptions.map((f) => (
              <Chip
                key={f}
                label={f}
                active={taskForm === f}
                onClick={() => onTaskForm(f)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
